/**
 * Estado e regras puras da tabela "Por empresa": busca, filtro por status,
 * ordenacao por coluna e paginacao.
 *
 * Decisoes nao obvias:
 *  1. Pensada para centenas de empresas: a lista inteira nunca vai para a
 *     tela. Busca e filtro recortam, a ordenacao decide quem vem primeiro e
 *     a paginacao entrega `TAMANHO_DA_PAGINA` linhas por vez.
 *  2. O estado mora na PAGINA (nao na tabela) para "O que fazer agora" poder
 *     abrir a tabela ja filtrada e ordenada (margem negativa primeiro, por
 *     exemplo). Mudar busca, filtro ou ordem volta para a primeira pagina.
 *  3. Ordenacao padrao por RECUPERADO BRUTO decrescente, a mesma que a API
 *     entrega. Empates desempatam pelo nome, para a ordem ser estavel.
 *  4. A busca ignora acento e caixa (`normalizar`): "codelab" acha "CodeLab".
 *  5. `margem_negativa` e um filtro ao lado dos status porque e a pergunta
 *     que a administracao faz ("quem esta me custando mais do que rende?"),
 *     nao um status da empresa.
 *  6. Custo/margem `null` (motor real, custo nao medido) nunca entra no filtro
 *     de margem negativa, ordena depois de qualquer valor medido e deixa a
 *     soma `null` -- somar como zero mostraria margem igual a comissao.
 */

import type { CompanyStatus } from '../../types';
import type { CompanyRow } from './types';

export const TAMANHO_DA_PAGINA = 8;

export type ColunaDaTabela = 'empresa' | 'status' | 'abandonados' | 'recuperados' | 'taxa' | 'recuperado' | 'comissao' | 'custos' | 'margem' | 'ultimoEvento';

export type FiltroDaTabela = 'todas' | CompanyStatus | 'margem_negativa';

export interface OrdemDaTabela {
  readonly coluna: ColunaDaTabela;
  readonly desc: boolean;
}

export interface EstadoDaTabela {
  readonly busca: string;
  readonly filtro: FiltroDaTabela;
  readonly ordem: OrdemDaTabela;
  readonly offset: number;
}

export const ESTADO_INICIAL_DA_TABELA: EstadoDaTabela = {
  busca: '',
  filtro: 'todas',
  ordem: { coluna: 'recuperado', desc: true },
  offset: 0,
};

export interface ColunaInfo {
  readonly rotulo: string;
  readonly numerica: boolean;
  /** Explicacao em termos de negocio; vira `title` do cabecalho. */
  readonly titulo: string;
}

export const COLUNAS: Readonly<Record<ColunaDaTabela, ColunaInfo>> = {
  empresa: { rotulo: 'Empresa', numerica: false, titulo: 'Nome, segmento e funis ligados agora' },
  status: { rotulo: 'Status', numerica: false, titulo: 'Ativa, em implantação ou pausada' },
  abandonados: { rotulo: 'Entradas', numerica: true, titulo: 'Checkouts abandonados e planos vencidos recebidos no período' },
  recuperados: { rotulo: 'Recuperados', numerica: true, titulo: 'Pagaram em até 7 dias depois de um contato; a taxa é sobre as entradas' },
  taxa: { rotulo: 'Taxa', numerica: true, titulo: 'Recuperados sobre entradas no período' },
  recuperado: { rotulo: 'Recuperado', numerica: true, titulo: 'Valor pago pelos leads recuperados (bruto)' },
  comissao: { rotulo: 'Comissão', numerica: true, titulo: 'Comissão do Converte Fácil, congelada por venda (ADR-001), e o percentual da empresa' },
  custos: { rotulo: 'Custos', numerica: true, titulo: 'Voz, WhatsApp, e-mail e infra rateada, com tarifas de exemplo. Clique para detalhar' },
  margem: { rotulo: 'Margem', numerica: true, titulo: 'Comissão menos custos, e a margem sobre a comissão' },
  ultimoEvento: { rotulo: 'Último evento', numerica: false, titulo: 'Último webhook ou acionamento visto, independente do período' },
};

export const ORDEM_DAS_COLUNAS: readonly ColunaDaTabela[] = ['empresa', 'status', 'abandonados', 'recuperados', 'taxa', 'recuperado', 'comissao', 'custos', 'margem', 'ultimoEvento'];

const ORDEM_DO_STATUS: Readonly<Record<CompanyStatus, number>> = { ativa: 0, onboarding: 1, pausada: 2 };

/** Valor nao medido ordena depois de qualquer valor real (decisao 6). */
const SEM_VALOR = -Number.MAX_SAFE_INTEGER;

export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

export function passaNoFiltro(row: CompanyRow, filtro: FiltroDaTabela): boolean {
  switch (filtro) {
    case 'todas':
      return true;
    case 'margem_negativa':
      return row.marginCents !== null && row.marginCents < 0;
    default:
      return row.status === filtro;
  }
}

export function filtrarLinhas(rows: readonly CompanyRow[], busca: string, filtro: FiltroDaTabela): readonly CompanyRow[] {
  const termo = normalizar(busca);
  return rows.filter((r) => passaNoFiltro(r, filtro) && (termo === '' || normalizar(r.companyName).includes(termo)));
}

function valorNumerico(row: CompanyRow, coluna: ColunaDaTabela): number {
  switch (coluna) {
    case 'abandonados':
      return row.abandonedCount;
    case 'recuperados':
      return row.recoveredCount;
    case 'taxa':
      // Sem entrada nao ha taxa; fica atras de qualquer taxa real.
      return row.abandonedCount === 0 ? -1 : row.recoveryRateBps;
    case 'recuperado':
      return row.recoveredAmountCents;
    case 'comissao':
      return row.commissionCents;
    case 'custos':
      return row.costCents ?? SEM_VALOR;
    case 'margem':
      return row.marginCents ?? SEM_VALOR;
    case 'status':
      return ORDEM_DO_STATUS[row.status];
    case 'ultimoEvento':
      return row.lastEvent === null ? 0 : new Date(row.lastEvent.at).getTime();
    case 'empresa':
      return 0;
  }
}

export function ordenarLinhas(rows: readonly CompanyRow[], ordem: OrdemDaTabela): readonly CompanyRow[] {
  const sinal = ordem.desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (ordem.coluna === 'empresa') {
      return sinal * a.companyName.localeCompare(b.companyName, 'pt-BR');
    }
    const diff = valorNumerico(a, ordem.coluna) - valorNumerico(b, ordem.coluna);
    return diff === 0 ? a.companyName.localeCompare(b.companyName, 'pt-BR') : sinal * diff;
  });
}

/** Clicar na coluna ativa inverte; clicar noutra comeca decrescente (texto comeca crescente). */
export function alternarOrdem(atual: OrdemDaTabela, coluna: ColunaDaTabela): OrdemDaTabela {
  if (atual.coluna === coluna) {
    return { coluna, desc: !atual.desc };
  }
  return { coluna, desc: coluna !== 'empresa' && coluna !== 'status' };
}

export interface SomaDasLinhas {
  readonly abandonados: number;
  readonly recuperados: number;
  readonly recuperadoCents: number;
  readonly comissaoCents: number;
  /** `null` quando alguma linha nao tem custo medido (decisao 6). */
  readonly custoCents: number | null;
  readonly margemCents: number | null;
}

export function somarLinhas(rows: readonly CompanyRow[]): SomaDasLinhas {
  return rows.reduce<SomaDasLinhas>(
    (acc, r) => ({
      abandonados: acc.abandonados + r.abandonedCount,
      recuperados: acc.recuperados + r.recoveredCount,
      recuperadoCents: acc.recuperadoCents + r.recoveredAmountCents,
      comissaoCents: acc.comissaoCents + r.commissionCents,
      custoCents: acc.custoCents === null || r.costCents === null ? null : acc.custoCents + r.costCents,
      margemCents: acc.margemCents === null || r.marginCents === null ? null : acc.margemCents + r.marginCents,
    }),
    { abandonados: 0, recuperados: 0, recuperadoCents: 0, comissaoCents: 0, custoCents: 0, margemCents: 0 },
  );
}
